import {CommonTypes, ArgValidator} from 'neo-common';
import {IClientHost} from './IClientHost';
import {IClientHostConsumer} from './IClientHostConsumer';
import {AutoplayAction, ClientHostDialogRequest, ClientHostDispatchExitRequest, ClientHostPaytableRequest, GamePlayAction} from './Types';
import {IApp} from '../GameEngine/IApp';
import {IBalance} from '../Communication/Game/Types/IBalance';

/**
 * Consumer for client host events
 */
export abstract class ClientHostConsumerBase implements IClientHostConsumer {
    protected _clientHost: IClientHost;
    protected _app: IApp;

    constructor(app: IApp) {
        ArgValidator.ensureIsNotNullOrUndefined(app, 'app');
        this._app = app;
    }

    /**
     * Handle dispatched action from client host
     * @param actionGroup - group name where action belongs to
     * @param action - name of action to handle
     */
    public abstract handleAction(actionGroup: string, action: string): void;

    /**
     * @method handleExit - Handle exit from client host
     * @param request - exit request info.
     * @param callback - function needs to be called when game finished its exit to notify client host about completed exit routine.
     */
    public abstract handleExit(request: ClientHostDispatchExitRequest, callback: CommonTypes.ErrorFunction): void;

    /**
     * Handle update to account balance.
     * @param {IBalance} balance
     */
    public abstract handleAccountUpdate(balance: IBalance, forceShow: boolean): void;

    /**
     * @method handleRefreshAccount - handle 'refresh account' request from client host
     */
    public abstract handleRefreshAccount(): void;

    /**
     * @method handleError - handle error from client host.
     * @param error - error message
     * @param data - structure with information about error
     */
    public abstract handleError(error: string, data?: any): void;

    /**
     * @method handlePaytableAction - handle paytable action (load, show, ...)
     * @param request - request to do with paytable
     */
    public abstract handlePaytableRequest(request: ClientHostPaytableRequest): void;

    /**
     * handle request to show dialog with message (optionally error)
     * @param request - show dialog request data
     */
    public abstract handleShowDialog(request: ClientHostDialogRequest): void;

    /**
     * handle request to show end game dialog with message
     */
    public abstract handleShowEndGameDialog(): void;

    /**
     * handle request to close currently visible dialog (if any)
     */
    public abstract handleCloseDialog(): void;

    /**
     * handle autoplay action request.
     */
    public abstract handleAutoplayAction(action: AutoplayAction): void;

    /**
     * handle gameplay action request.
     */
    public abstract handleGameplayAction(action: GamePlayAction): void;

    /**
     * handle request to block game.
     * @param {boolean} blocked - true to block game, false to unblock
     */
    public abstract handleBlockRequest(blocked: boolean): void;
}


export function updateClientHost(clientHostConsumer: IClientHostConsumer, clientHost: IClientHost): void {
    (clientHostConsumer as any)._clientHost = clientHost;
}


/** WEBPACK FOOTER **
 ** ./Engine/ClientHost/ClientHostConsumerBase.ts
 **/